import { z } from 'zod';
import { bpSchema, monthSchema, paiseSchema, signedPaiseSchema } from '../money';
import { objectIdSchema, optionalObjectId, optionalText, paginationQuerySchema } from './common';
import { TARGET_BASES } from './settings';

/**
 * Progress labels. "Behind" is deliberately not one of them: before the month ends an employee
 * below pace is simply "in progress". Only a closed month can be "not achieved".
 */
export const TARGET_STATUSES = ['no_target', 'not_started', 'in_progress', 'on_track', 'achieved', 'exceeded', 'not_achieved'] as const;
export type TargetStatus = (typeof TARGET_STATUSES)[number];
export const TARGET_STATUS_LABELS: Record<TargetStatus, string> = {
  no_target: 'No target',
  not_started: 'Not started',
  in_progress: 'In progress',
  on_track: 'On track',
  achieved: 'Target achieved',
  exceeded: 'Exceeded',
  not_achieved: 'Not achieved',
};

export const targetCreateSchema = z
  .object({
    month: monthSchema,
    /** One target per listed employee (bulk assignment)… */
    userIds: z.array(objectIdSchema).max(500).default([]),
    /** …or one shared department target. */
    departmentId: optionalObjectId(),
    targetPaise: paiseSchema.refine((v) => v > 0, 'Target must be more than zero'),
    /** Optional count target (e.g. number of conversions). */
    conversionsTarget: z.coerce.number().int().min(0).max(10_000).nullable().optional(),
    /** Override the workspace basis for these targets. */
    basis: z.enum(TARGET_BASES).nullable().optional(),
    notes: optionalText(1000),
  })
  .refine((v) => v.userIds.length > 0 || !!v.departmentId, { message: 'Choose employees or a department', path: ['userIds'] })
  .refine((v) => !(v.userIds.length > 0 && v.departmentId), { message: 'Assign to employees or to a department, not both', path: ['departmentId'] });
export type TargetCreateInput = z.input<typeof targetCreateSchema>;

export const targetReviseSchema = z
  .object({
    targetPaise: paiseSchema.refine((v) => v > 0, 'Target must be more than zero').optional(),
    conversionsTarget: z.coerce.number().int().min(0).max(10_000).nullable().optional(),
    basis: z.enum(TARGET_BASES).nullable().optional(),
    reason: z.string().trim().min(3).max(500),
  })
  .refine((v) => v.targetPaise !== undefined || v.conversionsTarget !== undefined || v.basis !== undefined, 'Nothing to change');

export const targetCancelSchema = z.object({ reason: z.string().trim().min(3).max(500) });

export const targetCopySchema = z.object({
  fromMonth: monthSchema,
  toMonth: monthSchema,
  /** Percentage change applied while copying (e.g. 1000 = +10%). */
  changeBp: z.coerce.number().int().min(-10_000).max(100_000).default(0),
});

export const targetListQuerySchema = paginationQuerySchema.extend({
  month: monthSchema.optional(),
  userId: objectIdSchema.optional(),
  departmentId: objectIdSchema.optional(),
  includeCancelled: z.enum(['1', 'true']).optional(),
});

export const targetOverviewQuerySchema = z.object({
  month: monthSchema.optional(),
  departmentId: objectIdSchema.optional(),
  branchId: objectIdSchema.optional(),
  designationId: objectIdSchema.optional(),
  userId: objectIdSchema.optional(),
  category: z.string().trim().max(80).optional(),
});
export type TargetOverviewQuery = z.input<typeof targetOverviewQuerySchema>;

export const targetExportQuerySchema = targetOverviewQuerySchema.extend({ format: z.enum(['xlsx', 'pdf']).default('xlsx') });

export const myTargetQuerySchema = z.object({ month: monthSchema.optional() });
export const targetHistoryQuerySchema = z.object({ userId: objectIdSchema.optional(), months: z.coerce.number().int().min(1).max(24).default(6) });

// ------------------------------------------------------------------ incentive plans & commissions
export const INCENTIVE_CALCULATIONS = ['slab', 'percentage', 'fixed'] as const;
export type IncentiveCalculation = (typeof INCENTIVE_CALCULATIONS)[number];

/** Highest slab whose threshold is reached applies to the whole achieved amount (no marginal maths). */
export const incentiveSlabSchema = z
  .object({
    minAchievementBp: z.coerce.number().int().min(0).max(100_000),
    rateBp: bpSchema.optional(),
    fixedPaise: paiseSchema.optional(),
  })
  .refine((s) => s.rateBp !== undefined || s.fixedPaise !== undefined, 'Give a rate or a fixed amount for each slab');

export const incentivePlanSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    description: optionalText(1000),
    active: z.boolean().default(true),
    effectiveFrom: monthSchema,
    effectiveTo: monthSchema.nullable().optional(),
    calculation: z.enum(INCENTIVE_CALCULATIONS),
    /** Achievement needed before anything is earned (10000 = 100% of target). */
    minAchievementBp: z.coerce.number().int().min(0).max(100_000).default(10_000),
    rateBp: bpSchema.optional(),
    fixedPaise: paiseSchema.optional(),
    slabs: z.array(incentiveSlabSchema).max(10).default([]),
    /** Extra fixed bonus per converted project in the month (project-specific bonus). */
    perProjectBonusPaise: paiseSchema.default(0),
    /** Maximum payout per person per month (0 = no cap). */
    capPaise: paiseSchema.default(0),
    eligibility: z
      .object({
        departmentIds: z.array(objectIdSchema).max(50).default([]),
        designationIds: z.array(objectIdSchema).max(50).default([]),
        userIds: z.array(objectIdSchema).max(500).default([]),
      })
      .default({ departmentIds: [], designationIds: [], userIds: [] }),
  })
  .refine((p) => p.calculation !== 'slab' || p.slabs.length > 0, { message: 'Add at least one slab', path: ['slabs'] })
  .refine((p) => p.calculation !== 'percentage' || p.rateBp !== undefined, { message: 'Enter the commission rate', path: ['rateBp'] })
  .refine((p) => p.calculation !== 'fixed' || p.fixedPaise !== undefined, { message: 'Enter the fixed incentive', path: ['fixedPaise'] })
  .refine((p) => !p.effectiveTo || p.effectiveTo >= p.effectiveFrom, { message: 'End month is before the start month', path: ['effectiveTo'] });
export type IncentivePlanInput = z.input<typeof incentivePlanSchema>;

export const COMMISSION_STATUSES = ['pending', 'under_review', 'approved', 'payable', 'paid', 'reversed'] as const;
export type CommissionStatus = (typeof COMMISSION_STATUSES)[number];
/** Allowed manual transitions. Paid commissions are corrected with a separate adjustment record. */
export const COMMISSION_TRANSITIONS: Record<CommissionStatus, CommissionStatus[]> = {
  pending: ['under_review', 'approved', 'reversed'],
  under_review: ['approved', 'pending', 'reversed'],
  approved: ['payable', 'reversed'],
  payable: ['paid', 'reversed'],
  paid: [],
  reversed: [],
};

export const commissionCalculateSchema = z.object({ month: monthSchema });
export const commissionTransitionSchema = z.object({
  to: z.enum(COMMISSION_STATUSES),
  note: optionalText(500),
  /** Payment reference when marking paid outside payroll. */
  reference: optionalText(120),
});
export const commissionListQuerySchema = paginationQuerySchema.extend({
  month: monthSchema.optional(),
  status: z.enum(COMMISSION_STATUSES).optional(),
  userId: objectIdSchema.optional(),
});
export const commissionManualSchema = z.object({
  userId: objectIdSchema,
  month: monthSchema,
  amountPaise: signedPaiseSchema.refine((v) => v !== 0, 'Amount cannot be zero'),
  reason: z.string().trim().min(3).max(500),
  projectId: optionalObjectId(),
});
